import { useMutation } from '@tanstack/react-query';
import {
    bulkChangeSubscriptionPeriodByFilter,
    ChangeSubscriptionPeriodByFilterRequest
} from '../../api/selection-list/bulkChangeSubscriptionPeriodByFilter';
import {
    bulkChangeSubscriptionPeriodByLineItemKeys,
    ChangeSubscriptionPeriodByLineItemKeysRequest
} from '../../api/selection-list/bulkChangeSubscriptionPeriodByLineItemKeys';
import { useDispatch } from 'react-redux';
import { ErrorResponse, globalError, showNotification } from '../../store/notifications/actions';
import { SubscriptionPeriodType } from '../../../models/Article';
import { invalidateSelectionListProducts } from './api/useSelectionListProducts';
import { deserializeFilter } from '../../shared/utils/deserializeFilter';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { deselectAll } from '../../store/productSelection/actions';
import { useTranslation } from 'react-i18next';
import { invalidateSelectionListAggregations } from './useSelectionListAggregations';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection
} from '../../shared/product-selection/ProductSelectionTypes';
import { convertLineItemKeysToDto } from '../../shared/utils/convertLineItemKey';

export const useBulkChangeSubscriptionPeriod = (
    selectionListId: string,
    filterQueryParam: string | undefined,
    productSelection: ProductSelection
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    return useMutation(
        ['selection-list', 'changeBulkSubscriptionPeriod', selectionListId],
        async (type: SubscriptionPeriodType) => {
            if (isExcludeProductSelection(productSelection)) {
                const body: ChangeSubscriptionPeriodByFilterRequest = {
                    filterRequest: deserializeFilter(filterQueryParam, {
                        includeKey: 'include',
                        excludeKey: 'exclude'
                    }),
                    excludedLineItemKeys: productSelection.excluded,
                    subscriptionPeriodType: type
                };

                return await bulkChangeSubscriptionPeriodByFilter({ selectionListId, body });
            } else if (isIncludeProductSelection(productSelection)) {
                const body: ChangeSubscriptionPeriodByLineItemKeysRequest = {
                    lineItemKeys: convertLineItemKeysToDto(productSelection),
                    subscriptionPeriodType: type
                };

                return await bulkChangeSubscriptionPeriodByLineItemKeys({ selectionListId, body });
            } else {
                throw new Error(t('notifications.buildAddProductsCallback.error'));
            }
        },
        {
            onSuccess: () => {
                dispatch(deselectAll());
                invalidateSelectionListProducts(selectionListId).then();
                invalidateSelectionListAggregations(selectionListId).then();
                invalidateSelectionListSummary(selectionListId).then();
                invalidateSelectionListGroupedItems(selectionListId).then();
                dispatch(showNotification(t('notifications.changeSubscriptionPeriodBulk.message')));
            },
            onError: (e: ErrorResponse) => {
                dispatch(globalError(e));
            }
        }
    );
};
